import { useEffect, useMemo } from 'react';
import { useActiveHexContext, useMapContext } from 'shared/context';
import { getHexPoints, getHexOffset } from 'shared/utils';
import { useWindowSize, useMiniHex } from 'shared/hooks';
import { HEX, MAP } from 'shared/constants';

export const useBigHex = () => {
  const { map } = useMapContext();
  const { ctxHex: ctx, setBigHexPath } = useActiveHexContext();
  const [windowWidth, windowHeight] = useWindowSize();
  const { generateMiniHexGrid } = useMiniHex();

  const zoom = map?.getZoom() ?? 0;
  const scale = useMemo(() => {
    return map?.getZoomScale(zoom, MAP.ZOOM) ?? 0;
  }, [zoom]);
  const hexWidth = useMemo(() => HEX.WIDTH * scale, [scale]);

  const clearCanvas = () =>
    ctx?.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  const applyBigHexStyle = () => {
    if (!ctx) return;
    ctx.lineWidth = 5;
    ctx.strokeStyle = 'black';
    ctx.fillStyle = 'transparent';
  };

  const getIsClickOutsideHex = (
    path: string,
    mouseX: number,
    mouseY: number,
  ) => {
    if (!ctx) return false;
    const hex = new Path2D(path);
    return !ctx.isPointInPath(hex, mouseX, mouseY);
  };

  const centerHex = () => {
    const points = getHexPoints(hexWidth);
    const offset = getHexOffset(ctx, hexWidth);
    const hexPoints: number[][] = points.map(point => [
      offset.x + point[0],
      offset.y + point[1],
    ]);
    return hexPoints;
  };

  const drawBigHex = () => {
    if (!ctx) return null;
    const hexPoints = centerHex();
    if (!hexPoints) return null;
    const hexPathStart = hexPoints.shift() as number[];
    const hexPathStr: string = hexPoints.reduce(
      (path, point) => `${path} L ${point[0]} ${point[1]}`,
      `M ${hexPathStart[0]} ${hexPathStart[1]}`,
    );
    const hexPath = new Path2D(hexPathStr);

    applyBigHexStyle();
    ctx.fill(hexPath);
    ctx.stroke(hexPath);

    return hexPathStr;
  };

  const handleBigHexChange = () => {
    if (!ctx || !map) return;
    clearCanvas();
    ctx.rect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.fillStyle = '#000000aa';
    ctx.fill();
    const zoom = map.getZoom();
    const scale = map.getZoomScale(zoom, MAP.ZOOM);
    const bigHexPathStr = drawBigHex() as string;
    setBigHexPath(bigHexPathStr);
    generateMiniHexGrid(scale);
  };

  const updateBigHexOnWindowResize = () => {
    if (!ctx) return;
    ctx.canvas.width = windowWidth;
    ctx.canvas.height = windowHeight;
    handleBigHexChange();
  };

  useEffect(() => {
    updateBigHexOnWindowResize();
  }, [windowWidth, windowHeight]);

  return {
    handleBigHexChange,
    getIsClickOutsideHex,
    centerHex,
  };
};
